<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'AgreementPrintPreview',
})
</script>
<template>
  <Modal v-model:visible="visible" :mask-closable="false" :class="bem()" title="协议预览" width="1200px" centered destroy-on-close @ok="handlePrint">
    <Skeleton active v-if="loading" :paragraph="{ rows: 10 }" />
    <iframe ref="iframeRef" :src="pdfSrc" :class="{ hidden: loading }" @load="handleIframeLoad" />
    <template #footer>
      <div v-if="isMulti" :class="bem('nav')">
        <Button :disabled="currentIndex <= 0" @click="currentIndex--">&lt; 上一个</Button>
        <span>{{ currentIndex + 1 }} / {{ urls.length }}</span>
        <Button :disabled="currentIndex >= urls.length - 1" @click="currentIndex++">下一个 &gt;</Button>
      </div>
      <Button v-if="isMulti" v-permission="$AK.JT_ZCH_DETAIL.DOWNLOAD" type="primary" ghost :loading="loading" @click="handleDownloadAll">
        全部下载
      </Button>
      <Button v-permission="$AK.JT_ZCH_DETAIL.DOWNLOAD" type="primary" ghost :loading="loading" @click="handleDownload">
        {{ isMulti ? '下载当前' : '下载' }}
      </Button>
      <Button v-permission="$AK.JT_ZCH_DETAIL.PRINT" type="primary" :loading="loading" @click="handlePrint">打印</Button>
    </template>
  </Modal>
</template>

<script lang="ts" setup name="AgreementPrintPreview">
import { createNamespace } from '@bd/vue3-utils'
import { Button, message, Modal, Skeleton } from 'ant-design-vue'
import { computed, ref } from 'vue'
const [_, bem] = createNamespace('AgreementPrintPreview')

/** 文件地址列表 */
const urls = ref<string[]>([])
/** 当前预览的文件索引 */
const currentIndex = ref(0)
/** 是否多文件模式 */
const isMulti = computed(() => urls.value.length > 1)

const pdfFileUrl = computed(() => urls.value[currentIndex.value])
const pdfSrc = computed(() => (pdfFileUrl.value ? `/pdf/web/viewer.html?file=${encodeURIComponent(pdfFileUrl.value)}` : ''))
const visible = ref(false)
const loading = ref(false)

/**
 * 打开预览弹窗
 * @param src 单个URL字符串或URL数组
 */
const open = (src: string | string[]) => {
  urls.value = Array.isArray(src) ? src : [src]
  currentIndex.value = 0
  loading.value = true
  visible.value = true
}
defineExpose({
  open
})
const iframeRef = ref<HTMLIFrameElement>()

const handleIframeLoad = () => {
  loading.value = false
}

const handlePrint = () => {
  const iframe = iframeRef.value
  if (iframe && iframe.contentWindow) {
    iframe.contentWindow.focus()
    iframe.contentWindow.print()
  }
}
const downloadFile = async (url: string) => {
  const response = await fetch(url)
  const blob = await response.blob()
  const blobUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = url.split('/').pop() || 'document.pdf'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(blobUrl)
}

const handleDownload = async () => {
  const url = pdfFileUrl.value
  if (!url) {
    message.error('文件不存在，无法下载')
    return
  }
  try {
    await downloadFile(url)
    message.success('下载已开始，请在浏览器下载管理中查看')
  } catch (error) {
    message.error('下载失败')
  }
}

const handleDownloadAll = async () => {
  if (urls.value.length === 0) {
    message.error('文件不存在，无法下载')
    return
  }
  try {
    for (const url of urls.value) {
      await downloadFile(url)
    }
    message.success('全部下载已开始，请在浏览器下载管理中查看')
  } catch (error) {
    message.error('下载失败')
  }
}
</script>

<style lang="less">
.agreement-print-preview {
  .ant-modal-body {
    padding: 0;

    iframe {
      display: block;
      width: 100%;
      height: calc(100vh - 200px);
      margin: 0;
      padding: 0;
      border: 0;
      outline: none;

      &.hidden {
        position: absolute;
        visibility: hidden;
      }
    }
  }

  &__nav {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    margin-right: 12px;

    span {
      min-width: 40px;
      font-size: 14px;
      text-align: center;
    }
  }
}
</style>
